// Activity Doors — the named activity types a course designer recognises
// without explanation (KULA-ACTIVITY-DOORS-TOP10.md, ratified 6 Aug 2026).
//
// The set is the ratified top three plus Debate and Card Sort (chosen over
// Pre-mortem — KULA-DOORS-SKETCH-PLAN.md, 7 Aug 2026). Each door is narrow on purpose:
// choosing the door IS the intent — the designer answers the hardest question
// in the pipeline (what do you want?) at the moment of choosing, which lets
// the generation run with far looser reins than the constrained Create
// pipeline. Under the ratified spanning fork (B), every door generates a
// sealed self-contained HTML interactive (`generated-interactive` slide type)
// rather than data for a fixed renderer.
//
// Adding a door later = one entry here (+ its generation prompt when the
// build pipeline lands). Doors 4–10 are specified in the same doc.

import type { DoorKey, DoorIntake, ThemeId } from '../../components/course-builder/slides/slideTypes';

export interface DoorDefinition {
  key: DoorKey;
  /** Shelf name — the plain designer-vernacular name, never internal codenames. */
  name: string;
  /** One-line pitch under the name on the door card. */
  tagline: string;
  /** Fuller description shown in the intake header. */
  description: string;
  /** Lucide icon name (rendered by the picker's door cards). */
  icon: string;
  /**
   * The door's own default world (one unique world per door — ruled 6 Aug).
   * Offered pre-selected in the intake; the designer can change it.
   */
  defaultWorld: ThemeId;
  /** Placeholder copy for the intake's context field — steers what "context" means for this door. */
  contextPlaceholder: string;
  /** Placeholder copy for the intake's goal field. */
  goalPlaceholder: string;
}

export const DOORS: DoorDefinition[] = [
  {
    key: 'scenario',
    name: 'Scenario',
    tagline: 'You’re in a role. You make calls. Consequences compound.',
    description:
      'An interactive branching situation: the learner takes a role, makes decisions under a genuine trade-off, and reaches an ending that depends on their path. Always branching — linear decision points are a quiz in costume.',
    icon: 'GitBranch',
    defaultWorld: 'neon-oracle',
    contextPlaceholder:
      'The situation and the decision it turns on — who the learner is, what’s at stake, what makes the call genuinely hard…',
    goalPlaceholder: 'What should making these calls teach the learner?',
  },
  {
    key: 'case-study',
    name: 'Case Study',
    tagline: 'A rich case to work through: brief, sources, judgement, debrief.',
    description:
      'A situated case the learner judges early, then re-judges as layers of the picture land — closing with the settled picture read from two or three seats. What makes a case study teach is the first judgement turning out incomplete.',
    icon: 'FolderOpen',
    defaultWorld: 'terra',
    contextPlaceholder:
      'The case material — the organisation or situation, what happened, the sources or evidence available…',
    goalPlaceholder: 'What should working the case teach the learner?',
  },
  {
    key: 'mark-the-work',
    name: 'Mark the Work',
    tagline: 'A struggling student’s flawed answer is on the desk. Coach them.',
    description:
      'Red-pen a struggling student’s answer, diagnose the belief underneath the mistake, coach them, and catch the residue in their revision. The protégé effect is the engine — you learn it by teaching it.',
    icon: 'PenTool',
    defaultWorld: 'lamplight',
    contextPlaceholder:
      'The topic and the misconception — what the flawed answer should get wrong, and the belief that produces that mistake…',
    goalPlaceholder: 'What should coaching this student teach the learner?',
  },
  {
    key: 'debate',
    name: 'Debate',
    tagline: 'Take a side. Build your case. Then argue the other side better.',
    description:
      'A genuinely contested claim: the learner commits to a side, builds their argument from an evidence pool seeded with red herrings, steelmans the opposition, then meets the expert’s weighing. A claim with a right answer is not a debate.',
    icon: 'Scale',
    defaultWorld: 'obsidian',
    contextPlaceholder:
      'The contested claim and the ground it’s fought on — what each side believes, and why a reasonable person could hold either…',
    goalPlaceholder: 'What should arguing both sides teach the learner?',
  },
  {
    key: 'card-sort',
    name: 'Card Sort',
    tagline: 'Sort the deck — but it’s stacked with near-misses.',
    description:
      'The learner sorts concrete cases into categories, but the deck is stacked with near-misses because the boundary is the lesson. Feedback reasons from the definition, never restates the label — the obvious cases teach nothing.',
    icon: 'LayoutGrid',
    defaultWorld: 'hyperpop',
    contextPlaceholder:
      'The categories and the boundary between them — what genuinely separates them, and what makes edge cases hard to place…',
    goalPlaceholder: 'What should drawing the line teach the learner?',
  },
];

export const getDoor = (key: DoorKey): DoorDefinition => {
  const door = DOORS.find((d) => d.key === key);
  if (!door) throw new Error(`Unknown activity door: ${key}`);
  return door;
};

/** The intake a door starts from — empty answers, the door's own default world. */
export const createDefaultIntake = (door: DoorDefinition): DoorIntake => ({
  context: '',
  useLessonSource: false,
  goal: '',
  length: 'short',
  world: door.defaultWorld,
});

/**
 * Worlds offered in the door intake's world pick. The six shipped theme
 * worlds + lamplight. Under fork B a world is a visual-language brief in the
 * generation prompt, not components — so any world here is generatable.
 */
export const DOOR_WORLDS: ThemeId[] = [
  'neon-oracle',
  'obsidian',
  'aurora',
  'terra',
  'hyperpop',
  'risograph',
  'lamplight',
];
